import type { JSX } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { Button, Modal, Notice, TextField } from '../components/ui.js';
import { IconMail } from '../components/icons.js';

/**
 * Вход и регистрация на одной странице.
 *
 * Переключатель вместо двух отдельных экранов: человек, промахнувшийся кнопкой,
 * не должен искать обратную дорогу. Заодно это первое знакомство с продуктом,
 * поэтому слева коротко сказано, зачем он вообще нужен.
 */

/**
 * Иконки первого экрана.
 *
 * Рисованные, а не эмодзи: эмодзи выглядят по-разному в каждой системе,
 * а на витрине продукта это первое, что выдаёт наспех собранную страницу.
 * Здесь же они принимают цвет темы и одинаковы у всех.
 */
const ICON_PATHS: Record<string, JSX.Element> = {
  link: (
    <>
      <path d="M9.5 13.5a4 4 0 0 0 5.7.4l2.6-2.4a4 4 0 0 0-5.4-5.9l-1.5 1.4" />
      <path d="M13.5 9.5a4 4 0 0 0-5.7-.4l-2.6 2.4a4 4 0 0 0 5.4 5.9l1.5-1.4" />
    </>
  ),
  group: (
    <>
      <circle cx="8.5" cy="8" r="2.6" />
      <circle cx="15.5" cy="9.5" r="2.1" />
      <path d="M3.5 18.5c0-2.6 2.2-4.4 5-4.4s5 1.8 5 4.4" />
      <path d="M15 14.3c2.7-.3 5 1.3 5.5 4.2" />
    </>
  ),
  phone: (
    <>
      <rect x="7" y="2.8" width="10" height="18.4" rx="2.6" />
      <path d="M10.6 5.6h2.8" />
      <path d="M12 18.2h.01" />
    </>
  ),
  hidden: (
    <>
      <path d="M3 3l18 18" />
      <path d="M10.6 10.7a2 2 0 0 0 2.8 2.8" />
      <path d="M6.6 6.8C4.6 8.1 3.1 10 2.5 12c1.5 3.6 5.2 6 9.5 6 1.5 0 2.9-.3 4.2-.9" />
      <path d="M9.9 6.2A9.8 9.8 0 0 1 12 6c4.3 0 8 2.4 9.5 6-.6 1.4-1.5 2.7-2.7 3.7" />
    </>
  ),
};

function Icon({ name }: { name: keyof typeof ICON_PATHS }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {ICON_PATHS[name]}
    </svg>
  );
}

const FEATURES = [
  {
    icon: 'link' as const,
    title: 'Ссылка на что угодно',
    text: 'Маркетплейс, квартира в новостройке, безымянный магазин горнолыжных масок. Фотографию и цену подставим сами.',
  },
  {
    icon: 'group' as const,
    title: 'Скидываемся вместе',
    text: 'На дорогой подарок друзья собирают сумму сообща — и видят, сколько уже собрано.',
  },
  {
    icon: 'phone' as const,
    title: 'Живёт в телефоне',
    text: 'Ставится на домашний экран Android и iPhone. Увидели вещь в магазине — отправили в приложение.',
  },
];

export function AuthPage(): JSX.Element {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState<string | null>(null);
  const [forgotBusy, setForgotBusy] = useState(false);

  /**
   * Запрос восстановления.
   *
   * Ответ сервера одинаков и для существующей почты, и для выдуманной —
   * поэтому и здесь показываем один и тот же текст. Сообщить «такого адреса
   * у нас нет» было бы удобнее, но это выдало бы, кто у нас зарегистрирован.
   */
  async function submitForgot(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setForgotBusy(true);
    try {
      const result = await api.auth.forgotPassword(forgotEmail.trim());
      setForgotSent(result.message);
    } catch (caught) {
      setForgotSent(
        caught instanceof ApiError && caught.status === 429
          ? caught.message
          : 'Если такой аккаунт есть, мы отправили на него письмо со ссылкой.',
      );
    } finally {
      setForgotBusy(false);
    }
  }

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      if (mode === 'login') {
        await login(email.trim(), password);
      } else {
        await register({
          email: email.trim(),
          password,
          displayName: displayName.trim(),
          ...(username.trim() ? { username: username.trim().toLowerCase() } : {}),
        });
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Не получилось. Попробуйте ещё раз.');
      setBusy(false);
    }
  }

  return (
    <div className="app-shell">
      <main className="app-main">
        <div className="hero">
          {/* Обещание словами. */}
          <section className="hero__pitch">
            <div className="row" style={{ marginBottom: 'var(--space-5)' }}>
              <span className="topbar__mark" style={{ width: 40, height: 40, fontSize: 19 }} aria-hidden="true">
                ✦
              </span>
              <span style={{ fontWeight: 750, fontSize: '1.2rem', letterSpacing: '-0.03em' }}>Загадарь</span>
            </div>

            <h1 className="hero__title">
              Вам уже выбрали подарок.
              <br />
              <span className="hero__accent">Вы об этом не узнаете.</span>
            </h1>

            <p className="hero__lead">
              Список желаний, где сюрприз остаётся сюрпризом. Друзья видят, что уже разобрали,
              и не дарят одно и то же дважды. Вы не видите ничего — и это не галочка
              в настройках, а устройство продукта.
            </p>
          </section>

          {/* То же обещание, но показанное: одна вещь двумя парами глаз. */}
          <section className="hero__aside">
            <div className="demo">
              <div className="demo__card">
                <div className="demo__label">Видит даритель</div>
                <div className="demo__thumb" aria-hidden="true">
                  🎧
                </div>
                <div className="strong small truncate">Наушники Sony</div>
                <div className="item__price" style={{ marginTop: 2, marginBottom: 'var(--space-3)' }}>
                  24 990 ₽
                </div>
                <div className="demo__taken">
                  <span className="demo__avatar" aria-hidden="true">
                    М
                  </span>
                  Забронировал Миша
                </div>
              </div>

              <div className="demo__card">
                <div className="demo__label">Видит именинник</div>
                <div className="demo__thumb" aria-hidden="true">
                  🎧
                </div>
                <div className="strong small truncate">Наушники Sony</div>
                <div className="item__price" style={{ marginTop: 2, marginBottom: 'var(--space-3)' }}>
                  24 990 ₽
                </div>
                <div className="demo__hidden">
                  <Icon name="hidden" />
                  Ни имени, ни отметки
                </div>
              </div>
            </div>

            <div className="panel">
              <div className="chip-row" style={{ marginBottom: 'var(--space-4)' }}>
              <button
                type="button"
                className={`chip ${mode === 'login' ? 'chip--active' : ''}`.trim()}
                onClick={() => {
                  setMode('login');
                  setError(null);
                }}
              >
                Войти
              </button>
              <button
                type="button"
                className={`chip ${mode === 'register' ? 'chip--active' : ''}`.trim()}
                onClick={() => {
                  setMode('register');
                  setError(null);
                }}
              >
                Создать аккаунт
              </button>
            </div>

            <form className="stack" onSubmit={handleSubmit}>
              {mode === 'register' && (
                <>
                  <TextField
                    label="Как вас зовут"
                    placeholder="Имя, которое увидят друзья"
                    value={displayName}
                    onChange={(event) => setDisplayName(event.target.value)}
                    autoComplete="name"
                    required
                  />
                  <TextField
                    label="Имя пользователя"
                    placeholder="masha"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    hint="По нему вас найдут друзья. Латиница, цифры и _. Можно добавить потом."
                    autoComplete="username"
                    pattern="[a-zA-Z][a-zA-Z0-9_]{2,31}"
                  />
                </>
              )}

              <TextField
                label="Почта"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
              />

              <TextField
                label="Пароль"
                type="password"
                placeholder={mode === 'register' ? 'Минимум 8 символов' : ''}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                minLength={mode === 'register' ? 8 : undefined}
                required
              />

              {error && (
                <Notice tone="danger">
                  {error}
                </Notice>
              )}

              <Button type="submit" variant="primary" size="lg" loading={busy} block>
                {mode === 'login' ? 'Войти' : 'Создать аккаунт'}
              </Button>

              {mode === 'register' && (
                <p className="tiny faint center">
                  Создавая аккаунт, вы соглашаетесь с{' '}
                  <Link to="/privacy" className="inline-link">обработкой данных</Link>. Коротко: храним почту и то,
                  что вы сами внесли; не продаём и не показываем рекламу.
                </p>
              )}

              {mode === 'login' && (
                <button
                  type="button"
                  className="link-button small center"
                  onClick={() => {
                    setForgotSent(null);
                    setForgotOpen(true);
                  }}
                >
                  Забыли пароль?
                </button>
              )}
            </form>
            </div>
          </section>

          {/* Подробности — тем, кому обещания оказалось мало. */}
          <ul className="hero__points">
            {FEATURES.map(({ icon, title, text }) => (
              <li key={title} className="hero__point">
                <span className="hero__point-icon" aria-hidden="true">
                  <Icon name={icon} />
                </span>
                <span>
                  <span className="strong small" style={{ display: 'block' }}>
                    {title}
                  </span>
                  <span className="tiny muted">{text}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </main>

      <Modal open={forgotOpen} title="Восстановление пароля" onClose={() => setForgotOpen(false)}>
        {forgotSent ? (
          <div className="stack">
            <Notice tone="success" icon={<IconMail size={19} />}>
              {forgotSent}
            </Notice>
            <p className="small muted">
              Ссылка одноразовая и действует один час. Не пришло — проверьте папку со спамом.
            </p>
            <Button variant="ghost" onClick={() => setForgotOpen(false)} block>
              Понятно
            </Button>
          </div>
        ) : (
          <form className="stack" onSubmit={submitForgot}>
            <p className="small muted">
              Укажите почту, на которую заведён аккаунт. Пришлём ссылку для установки нового пароля.
            </p>
            <TextField
              label="Почта"
              type="email"
              value={forgotEmail}
              onChange={(event) => setForgotEmail(event.target.value)}
              autoComplete="email"
              required
              autoFocus
            />
            <div className="row">
              <Button variant="ghost" type="button" onClick={() => setForgotOpen(false)} block>
                Отмена
              </Button>
              <Button variant="primary" type="submit" loading={forgotBusy} block>
                Прислать ссылку
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
